import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Search } from "lucide-react";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { TipoProdutosTable } from "@/components/tipo-produto/TipoProdutosTable";
import { TipoProdutoFormDialog } from "@/components/tipo-produto/TipoProdutoFormDialog";
import { TipoProdutoViewDialog } from "@/components/tipo-produto/TipoProdutoViewDialog";
import { TipoProdutoDeleteDialog } from "@/components/tipo-produto/TipoProdutoDeleteDialog";
import { tipoProdutosAdapter } from "@/data";
import { TipoProduto } from "@/types/tipo-produto";
import { TipoProdutoFormData } from "@/schemas/tipoProdutoSchema";
import { toast } from "sonner";
import { can } from "@/lib/rbac";
import { useSession } from "@/state/session";
import { LoadingSpinner } from "@/components/ui/loading-spinner";

export default function Produtos() {
  const { user } = useSession();
  const queryClient = useQueryClient();

  const [search, setSearch] = useState("");
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedProduto, setSelectedProduto] = useState<TipoProduto | null>(null);
  const [formMode, setFormMode] = useState<"create" | "edit">("create");

  const canCreate = can(user?.role, "tipo_produtos", "create");
  const canEdit = can(user?.role, "tipo_produtos", "edit");
  const canDelete = can(user?.role, "tipo_produtos", "delete");

  const { data: produtosData, isLoading } = useQuery({
    queryKey: ["tipo-produtos", search],
    queryFn: () => tipoProdutosAdapter.list({ search, limit: 100 }),
  });

  const createMutation = useMutation({
    mutationFn: (data: TipoProdutoFormData) => tipoProdutosAdapter.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tipo-produtos"] });
      toast.success("Produto criado com sucesso!");
      setFormDialogOpen(false);
    },
    onError: (error: any) => {
      toast.error(error.message || "Erro ao criar produto");
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: TipoProdutoFormData }) => tipoProdutosAdapter.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tipo-produtos"] });
      toast.success("Produto atualizado com sucesso!");
      setFormDialogOpen(false);
    },
    onError: (error: any) => {
      toast.error(error.message || "Erro ao atualizar produto");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => tipoProdutosAdapter.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tipo-produtos"] });
      toast.success("Produto excluído com sucesso!");
      setDeleteDialogOpen(false);
    },
    onError: (error: any) => {
      toast.error(error.message || "Erro ao excluir produto");
    },
  });

  const handleCreate = () => {
    setFormMode("create");
    setSelectedProduto(null);
    setFormDialogOpen(true);
  };

  const handleEdit = (produto: TipoProduto) => {
    setFormMode("edit");
    setSelectedProduto(produto);
    setFormDialogOpen(true);
  };

  const handleView = (produto: TipoProduto) => {
    setSelectedProduto(produto);
    setViewDialogOpen(true);
  };

  const handleDelete = (produto: TipoProduto) => {
    setSelectedProduto(produto);
    setDeleteDialogOpen(true);
  };

  const handleFormSubmit = (data: TipoProdutoFormData) => {
    if (formMode === "create") {
      createMutation.mutate(data);
    } else if (selectedProduto) {
      updateMutation.mutate({ id: selectedProduto.id_tipo_produto, data });
    }
  };

  const handleDeleteConfirm = () => {
    if (selectedProduto) {
      deleteMutation.mutate(selectedProduto.id_tipo_produto);
    }
  };

  const produtos = produtosData?.items || [];

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold">Vitrine</h1>
          <p className="text-muted-foreground">Gerencie os produtos disponíveis para venda</p>
        </div>

        <div className="flex justify-between items-center gap-4">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar por nome ou categoria..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          {canCreate && (
            <Button onClick={handleCreate}>
              <Plus className="mr-2 h-4 w-4" />
              Novo Item Venda
            </Button>
          )}
        </div>

        <Card>
          <CardHeader>
            <CardTitle>
              {produtos.length} {produtos.length === 1 ? "produto" : "produtos"}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex justify-center py-8">
                <LoadingSpinner size="lg" text="Carregando produtos..." />
              </div>
            ) : (
              <TipoProdutosTable
                produtos={produtos}
                onView={handleView}
                onEdit={handleEdit}
                onDelete={handleDelete}
                canEdit={canEdit}
                canDelete={canDelete}
              />
            )}
          </CardContent>
        </Card>

        <TipoProdutoFormDialog
          open={formDialogOpen}
          onOpenChange={setFormDialogOpen}
          onSubmit={handleFormSubmit}
          produto={selectedProduto}
          mode={formMode}
        />

        <TipoProdutoViewDialog open={viewDialogOpen} onOpenChange={setViewDialogOpen} produto={selectedProduto} />

        <TipoProdutoDeleteDialog
          open={deleteDialogOpen}
          onOpenChange={setDeleteDialogOpen}
          onConfirm={handleDeleteConfirm}
          produto={selectedProduto}
        />
      </div>
    </Layout>
  );
}
